import { cn } from '../../lib/utils';
import { getModelIconConfig, getProviderIconConfig } from '../../lib/model-icons';

interface ModelProviderIconProps {
  /** Canonical provider id (`anthropic`, `openai`, `fireworks-ai`, etc.). */
  provider?: string | null;
  /** Model id/name; used to derive the brand when no `provider` is given. */
  model?: string | null;
  size?: 'sm' | 'md';
  /** Wrap the glyph in a tinted, rounded box (like an avatar). Defaults to false. */
  withBackground?: boolean;
  className?: string;
}

const containerClasses = {
  sm: 'h-6 w-6',
  md: 'h-8 w-8',
} as const;

const iconClasses = {
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
} as const;

/**
 * Renders the brand icon for a provider or model, sourced from
 * `@lobehub/icons` via the shared resolver. Prefer passing `provider` when the
 * canonical provider id is known (e.g. on the agent-models page); otherwise the
 * brand is derived from `model`.
 */
export function ModelProviderIcon({
  provider,
  model,
  size = 'sm',
  withBackground = false,
  className,
}: ModelProviderIconProps) {
  const config = provider ? getProviderIconConfig(provider) : getModelIconConfig(model);
  const { icon: Icon, color, bgColor, isBrandSvg } = config;
  const testId = `model-provider-icon-${provider ?? model ?? 'unknown'}`;

  if (!withBackground) {
    return (
      <span data-testid={testId} className={cn('inline-flex shrink-0', className)}>
        <Icon className={cn(iconClasses[size], !isBrandSvg && color)} />
      </span>
    );
  }

  return (
    <span
      data-testid={testId}
      className={cn(
        'flex shrink-0 items-center justify-center rounded-md ring-1 ring-border/60',
        containerClasses[size],
        bgColor,
        className,
      )}
    >
      <Icon className={cn(iconClasses[size], !isBrandSvg && color)} />
    </span>
  );
}
